import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

function ViewAsLog({ open, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (open) api.get("/auth/impersonate/log").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="viewas-log-dialog">
        <DialogHeader><DialogTitle>View-as audit log</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Every "Troubleshoot as" session — who viewed whom and when (Sydney time). Sessions auto-expire after 1 hour.</p>
        {!rows ? <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          : rows.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground" data-testid="viewas-log-empty">No view-as sessions recorded yet.</p>
          : (
          <div className="max-h-[55vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Viewed</TableHead>
                  <TableHead>Ended</TableHead>
                  <TableHead>Token expiry</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.session_id || r.at} data-testid={`viewas-log-row-${r.session_id || ""}`}>
                    <TableCell className="whitespace-nowrap text-xs">{fmt(r.at)}</TableCell>
                    <TableCell className="text-xs">{r.admin_name || r.admin_email}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium">{r.target_name || r.target_email}</div>
                      <div className="text-muted-foreground">{r.target_role}{r.target_type ? ` · ${r.target_type}` : ""} · {r.target_email}</div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      {r.ended_at ? fmt(r.ended_at) : <Badge variant="outline" className="border-kilted-gold text-kilted-green">Not ended</Badge>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{fmt(r.expires_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ImpersonateSwitch() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [users, setUsers] = useState([]);
  const [logOpen, setLogOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/admin/customers"), api.get("/admin/drivers")])
      .then(([c, d]) => {
        const merged = [
          ...d.data.map((u) => ({ ...u, kind: "Driver" })),
          ...c.data.filter((u) => u.role === "customer").map((u) => ({ ...u, kind: u.customer_type === "florist" ? "Florist" : u.customer_type === "on_account" ? "Account" : "Customer" })),
        ];
        const seen = new Set();
        setUsers(merged.filter((u) => !seen.has(u.id) && seen.add(u.id)));
      })
      .catch(() => {});
  }, []);

  const go = async (id) => {
    try {
      const { data } = await api.post(`/auth/impersonate/${id}`);
      localStorage.setItem("kc_admin_backup", localStorage.getItem("kc_token"));
      await setSession(data.token, data.user);
      toast.success(`Now viewing as ${data.user.name} — use the yellow bar to return`);
      navigate(data.user.role === "driver" ? "/driver" : "/dashboard");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't switch account"); }
  };

  return (
    <div className="flex items-center gap-1.5">
      <Select onValueChange={go}>
        <SelectTrigger className="h-10 w-52" data-testid="impersonate-select">
          <SelectValue placeholder="Troubleshoot as…" />
        </SelectTrigger>
        <SelectContent>
          {users.map((u) => (
            <SelectItem key={u.id} value={u.id} data-testid={`impersonate-option-${u.id}`}>{u.name} · {u.kind}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" title="View-as audit log" onClick={() => setLogOpen(true)} data-testid="viewas-log-button">
        <History className="h-4 w-4" />
      </Button>
      <ViewAsLog open={logOpen} onClose={() => setLogOpen(false)} />
    </div>
  );
}
